import { tsoPqTheme } from '@assets/style/customTsoPqtheme';
import IconButtonTsoPq from '@components/Buttons/IconButtonTsoPq';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Divider, IconButton, Popper, styled, Typography, TypographyProps } from '@mui/material';
import {
  LocalizationProvider,
  PickersActionBarAction,
  PickersCalendarHeaderProps,
  PickersDay,
} from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/de';
import { forwardRef, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface StyledSupportingTextProps extends TypographyProps {
  error?: boolean;
}

type DatepickerHeaderOwnProps = {
  date?: Dayjs | null;
  setDate: (date: Dayjs | null) => void;
  referenceDate?: Dayjs;
};

type DatePickerHeaderProps = PickersCalendarHeaderProps & DatepickerHeaderOwnProps;

const StyledCalendarHeader = styled(Box)(() => {
  return {
    display: 'flex',
    flexDirection: 'column',
    gap: tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_4,
  };
});

const StyledHeaderContainer = styled(Box)(() => {
  return {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    color: tsoPqTheme.palette.customTextColor.color_primary_font_ft_prim_enabled,
  };
});

const StyledPopper = styled(Popper)(() => {
  return {
    zIndex: 1400,
    '& .MuiPaper-root': {
      padding: tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_8,

      '& .MuiMultiSectionDigitalClock-root': {
        width: '6rem',
        minHeight: '22.875rem',

        '& .MuiMultiSectionDigitalClockSection-root': {
          lineHeight: '1.375rem',
          color: tsoPqTheme.palette.customTextColor.color_neutral_font_ft_nrtrl_enabled,
          minHeight: '22.875rem',

          '& .Mui-selected': {
            background: tsoPqTheme.palette.background.primary.color_primary_background_bg_prim_enabled,
            outline: 'none',
          },

          '& .MuiButtonBase-root': {
            width: '2.25rem',
            height: '2.25rem',
            padding: '0',
            fontSize: tsoPqTheme.typography.fontsize_label_label_large,
            fontFamily: tsoPqTheme.typography.openSans,
            fontWeight: 400,
            borderRadius: tsoPqTheme.shape.border_radius_tso_border_radius_2,

            '&:hover': {
              background: tsoPqTheme.palette.background.primary.color_primary_background_bg_prim_hover_lt,
            },
          },
        },
      },
    },

    '& .MuiDateCalendar-root': {
      minHeight: '22.875rem',
    },

    '& .MuiButton-root': {
      display: 'block',
      padding: `${tsoPqTheme.spacingValues.buttonSpacing.spacing_button_small_button_sm_padding_s} ${tsoPqTheme.spacingValues.buttonSpacing.spacing_button_small_button_sm_padding_l}`,
      background: tsoPqTheme.palette.background.primary.color_primary_background_bg_prim_enabled,
      color: tsoPqTheme.palette.customTextColor.color_font_ft_white,
      fontFamily: tsoPqTheme.typography.openSans,
      fontSize: tsoPqTheme.typography.fontsize_body_body_medium,
      fontWeight: 400,
      lineHeight: '1rem',

      '&:active': {
        outline: 'none',
      },

      '&:focus': {
        outline: 'none',
      },
    },

    '& .MuiDialogActions-root': {
      padding: 0,
    },
  };
});

const StyledDay = styled(PickersDay)(() => {
  return {
    fontFamily: tsoPqTheme.typography.openSans,
    fontSize: tsoPqTheme.typography.fontsize_label_label_large,
    fontWeight: 400,
    lineHeight: '1.375rem',
    borderRadius: tsoPqTheme.shape.border_radius_tso_border_radius_2,
    color: tsoPqTheme.palette.customTextColor.color_neutral_font_ft_nrtrl_enabled,

    '&:hover': {
      background: tsoPqTheme.palette.background.primary.color_primary_background_bg_prim_hover_lt,
    },

    ':active': {
      background: tsoPqTheme.palette.background.primary.color_primary_background_bg_prim_pressed_lt,
    },

    '&.Mui-selected': {
      background: tsoPqTheme.palette.background.primary.color_primary_background_bg_prim_enabled,
      outline: 'none',
      '&:hover': {
        background: tsoPqTheme.palette.background.primary.color_primary_background_bg_prim_hover,
      },
      '&:focus': {
        background: tsoPqTheme.palette.background.primary.color_primary_background_bg_prim_enabled,
      },
      '&:active': {
        background: tsoPqTheme.palette.background.primary.color_primary_background_bg_prim_pressed,
      },
    },

    '&.MuiButtonBase-root': {
      outline: 'none',
    },

    '&.MuiPickersDay-today': {
      borderColor: tsoPqTheme.palette.stroke.color_primary_stroke_str_prim_accent,
    },
  };
});

const StyledIconButton = styled(IconButton)(() => {
  return {
    color: tsoPqTheme.palette.customTextColor.color_primary_font_ft_prim_accent,
    display: 'flex',
    justifyContent: 'center',
    borderRadius: tsoPqTheme.shape.border_radius_tso_border_radius_4,
    padding: '0.625rem',

    '&:hover': {
      background: tsoPqTheme.palette.background.primary.color_primary_background_bg_prim_hover_lt,
    },

    '&:active': {
      background: tsoPqTheme.palette.background.primary.color_primary_background_bg_prim_pressed_lt,
    },

    '&:focus': { outline: 'none' },

    '& svg': { fontSize: '1.5rem' },
  };
});

const StyledArrowForwardIosIcon = styled(ArrowForwardIosIcon)(() => {
  return {
    transform: 'rotate(180deg)',
  };
});

const StyledDatePicker = styled(DateTimePicker, {
  shouldForwardProp: (prop) => prop !== 'width',
})(({ width, error, disabled }: { width: string | undefined; error: boolean; disabled: boolean }) => {
  return {
    width: width ?? 'auto',
    borderRadius: tsoPqTheme.shape.border_radius_tso_border_radius_4,
    color: tsoPqTheme.palette.customTextColor.color_neutral_font_ft_nrtrl_enabled,
    ':hover': {
      background: tsoPqTheme.palette.background.neutral.color_neutral_background_bg_ntrl_enabled_lt,
    },

    '& .MuiOutlinedInput-root': {
      background: tsoPqTheme.palette.background.other.color_other_background_white,
      fontSize: tsoPqTheme.typography.fontsize_body_body_medium,

      '& fieldset': {
        borderColor: error && tsoPqTheme.palette.customError.color_signal_error_error,
      },

      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: tsoPqTheme.palette.stroke.color_neutral_stroke_str_ntrl_accent,
      },

      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        border: `1px solid ${tsoPqTheme.palette.stroke.color_neutral_stroke_str_ntrl_accent}`,
      },
    },

    '& .MuiPickersOutlinedInput-root': {
      background: tsoPqTheme.palette.background.other.color_other_background_white,
      fontSize: tsoPqTheme.typography.fontsize_body_body_medium,

      '& fieldset': {
        borderColor: error && tsoPqTheme.palette.customError.color_signal_error_error,
      },

      '&:hover .MuiPickersOutlinedInput-notchedOutline': {
        borderColor: tsoPqTheme.palette.stroke.color_neutral_stroke_str_ntrl_accent,
      },

      '&.Mui-focused .MuiPickersOutlinedInput-notchedOutline': {
        border: `1px solid ${tsoPqTheme.palette.stroke.color_neutral_stroke_str_ntrl_accent}`,
      },
    },

    '& .MuiButtonBase-root ': {
      color: disabled
        ? tsoPqTheme.palette.background.neutral.color_neutral_background_bg_ntrl_accent
        : tsoPqTheme.palette.customTextColor.color_neutral_font_ft_nrtrl_enabled,
      borderRadius: '0.25rem',
      padding: '0.25rem',
      margin: '0',

      '&:hover': {
        background: tsoPqTheme.palette.background.primary.color_primary_background_bg_prim_hover_lt,
      },

      '&:active': {
        outline: 'none',
        background: tsoPqTheme.palette.background.primary.color_primary_background_bg_prim_pressed_lt,
      },

      '&:focus': {
        outline: 'none',
      },
    },

    '& .MuiInputBase-root ': {
      padding: `0 ${tsoPqTheme.spacingValues.componentSpacing.spacing_component_padding_8} 0 0`,
      background: disabled && tsoPqTheme.palette.background.neutral.color_neutral_background_bg_ntrl,
      height: '2.25rem',
    },

    '& .MuiPickersSectionList-root': {
      padding: `${tsoPqTheme.spacingValues.componentSpacing.spacing_component_padding_8} 0 ${tsoPqTheme.spacingValues.componentSpacing.spacing_component_padding_8} ${tsoPqTheme.spacingValues.componentSpacing.spacing_component_padding_8}`,
    },

    '& .MuiPickersInputBase-root ': {
      padding: `0 ${tsoPqTheme.spacingValues.componentSpacing.spacing_component_padding_8} 0 0`,
      background: disabled && tsoPqTheme.palette.background.neutral.color_neutral_background_bg_ntrl,
      height: '2.25rem',
    },
  };
});

const StyledDatePickerContainer = styled(Box)(() => {
  return {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.375rem',
  };
});

export const StyledLabel = styled(Typography)(() => {
  return {
    display: 'flex',
    color: tsoPqTheme.palette.customTextColor.color_font_ft_helping_text,
    fontSize: tsoPqTheme.typography.fontsize_body_body_small,
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '1rem',
    padding: '0 0 0 0.25rem',
  };
});

export const StyledMandatory = styled('span')({
  color: tsoPqTheme.palette.customError.color_signal_error_error,
  fontFamiliy: tsoPqTheme.typography.openSans,
  fontSize: tsoPqTheme.typography.fontsize_body_body_small,
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '0.875rem',
});

export const StyledSupportingText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'error',
})<StyledSupportingTextProps>(({ error }) => {
  return {
    color: error
      ? tsoPqTheme.palette.customError.color_signal_error_error
      : tsoPqTheme.palette.customTextColor.color_font_ft_helping_text,
    fontFamily: tsoPqTheme.typography.openSans,
    fontSize: tsoPqTheme.typography.fontsize_body_body_small,
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '0.875rem',
    padding: '0 0 0 0.5rem',
  };
});

export const StyledInfoIcon = styled(IconButtonTsoPq)(() => {
  return {
    margin: `0 ${tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_4}`,
    alignSelf: 'center',

    '&:hover': {
      cursor: 'pointer',
    },
  };
});

const DatePickerHeader = ({ date, setDate, referenceDate, currentMonth, onMonthChange }: DatePickerHeaderProps) => {
  const { t } = useTranslation();

  const headerDate = (currentMonth ?? date ?? referenceDate ?? dayjs()) as Dayjs;

  const changeYear = (amount: number): void => {
    const newDate = headerDate.add(amount, 'year');
    setDate(newDate);
    onMonthChange?.(newDate, amount > 0 ? 'left' : 'right');
  };

  const changeMonth = (amount: number): void => {
    const newDate = headerDate.add(amount, 'month');
    setDate(newDate);
    onMonthChange?.(newDate, amount > 0 ? 'left' : 'right');
  };

  return (
    <StyledCalendarHeader>
      <StyledHeaderContainer>
        <StyledIconButton onClick={() => changeYear(-1)} disableRipple>
          <StyledArrowForwardIosIcon />
        </StyledIconButton>
        <Typography>{headerDate?.format('YYYY') ?? dayjs().format('YYYY')}</Typography>
        <StyledIconButton onClick={() => changeYear(1)} disableRipple>
          <ArrowForwardIosIcon />
        </StyledIconButton>
      </StyledHeaderContainer>
      <StyledHeaderContainer>
        <StyledIconButton onClick={() => changeMonth(-1)} disableRipple>
          <StyledArrowForwardIosIcon />
        </StyledIconButton>
        <Typography>
          {t(
            `calendar.month.${headerDate?.locale('en').format('MMMM').substring(0, 3).toLowerCase() ?? dayjs().locale('en').format('MMMM').substring(0, 3).toLowerCase()}`
          )}
        </Typography>
        <StyledIconButton onClick={() => changeMonth(1)} disableRipple>
          <ArrowForwardIosIcon />
        </StyledIconButton>
      </StyledHeaderContainer>
      <Divider />
    </StyledCalendarHeader>
  );
};

const CustomStyledDay = (props: any) => <StyledDay disableRipple {...props} />;

const CustomDatePickerHeader = (props: any) => (
  <DatePickerHeader date={props.date} setDate={props.setDate} {...props} />
);

const setTimeValue = (type: string, setValue: any, name: string | undefined, date: dayjs.Dayjs) => {
  if (type == 'datepicker') {
    setValue(name, dayjs(date).format('YYYY-MM-DD'));
  }
  if (type == 'yearpicker') {
    setValue(name, dayjs(date).year());
  }
  if (type == 'timepicker') {
    setValue(name, dayjs(date).format('HH:mm'));
  }
  if (type == 'datetimepicker') {
    setValue(name, dayjs(date).format('YYYY-MM-DDTHH:mm'));
  }
};

const viewsMap = {
  datepicker: ['year', 'month', 'day'],
  yearpicker: ['year'],
  timepicker: ['hours', 'minutes'],
  datetimepicker: ['year', 'month', 'day', 'hours', 'minutes'],
} as const;

const DatepickerTsoPq = forwardRef(
  (
    {
      enableFutureDates = false,
      width,
      mandatory,
      label,
      supportingText,
      error,
      actionBarProps,
      type,
      name,
      watch,
      setValue,
      minDateTime,
      maxDateTime,
      referenceDate,
      handleChange,
      date,
      disabled,
      shouldDisableDate,
      info = undefined,
    }: {
      enableFutureDates: boolean;
      width?: string;
      mandatory?: boolean;
      label: string;
      supportingText: string;
      error: boolean;
      actionBarProps?: PickersActionBarAction[];
      type: 'datepicker' | 'yearpicker' | 'timepicker' | 'datetimepicker';
      name?: string;
      watch?: any;
      setValue?: any;
      maxDateTime?: Dayjs;
      minDateTime?: Dayjs;
      referenceDate?: Dayjs;
      handleChange?: (date: Dayjs) => void;
      date?: string;
      disabled?: boolean;
      shouldDisableDate?: (date: Dayjs) => void;
      info?: string;
    },
    ref
  ) => {
    const [open, setOpen] = useState<boolean>(false);
    const [errorReason, setErrorReason] = useState<string>();

    const dateFormat = 'de';

    const { t } = useTranslation();

    const selectedValue = watch?.(name);

    const selectedDate = useMemo(() => {
      if (!selectedValue) return null;
      if (type === 'datepicker') {
        return dayjs(selectedValue);
      }
      if (type === 'timepicker') {
        return dayjs(selectedValue);
      }
      if (type === 'yearpicker') {
        return dayjs(`${selectedValue}-01-01`);
      }
      if (type === 'datetimepicker') {
        return dayjs(selectedValue);
      }
      return null;
    }, [selectedValue, type]);

    const externalDate = useMemo(() => (date ? dayjs(date) : null), [date]);
    const pickerValue = selectedDate ?? externalDate;

    const setSelectedDate = (date: Dayjs | null) => {
      if (setValue) {
        if (date) {
          setTimeValue(type, setValue, name, date);
        } else {
          setValue(name, undefined);
        }
      }

      if (handleChange && date) {
        handleChange(date);
      }
    };

    const combinedShouldDisableDate = (date: Dayjs) => {
      const today = dayjs().startOf('day');
      if (!enableFutureDates && date.isAfter(today)) return true;
      if (shouldDisableDate) {
        return shouldDisableDate(date);
      }
      return false;
    };

    return (
      <StyledDatePickerContainer>
        <StyledLabel>
          {label}
          {mandatory && <StyledMandatory>*</StyledMandatory>}
          {info && (
            <StyledInfoIcon
              customIcon={<InfoOutlinedIcon fontSize="inherit" />}
              buttonType="transparentNeutralNoEffect"
              sizeVariant="xs"
              tooltip={info}
            />
          )}
        </StyledLabel>
        <LocalizationProvider
          dateAdapter={AdapterDayjs}
          adapterLocale={dateFormat}
          localeText={{ okButtonLabel: t('calendar.button.accept') }}
        >
          <StyledDatePicker
            ampm={false}
            error={error}
            width={width}
            minDateTime={minDateTime}
            maxDateTime={maxDateTime}
            disabled={disabled ?? false}
            referenceDate={referenceDate}
            shouldDisableDate={combinedShouldDisableDate}
            dayOfWeekFormatter={(date) => date.format('dd')}
            value={pickerValue}
            views={viewsMap[type]}
            onChange={(date) => setSelectedDate(date)}
            onError={(reason) => setErrorReason(reason ?? '')}
            closeOnSelect={true}
            open={open}
            onOpen={() => {
              setOpen(true);
            }}
            onClose={() => setOpen(false)}
            onAccept={() => setOpen(false)}
            slots={{
              calendarHeader:
                type == 'yearpicker'
                  ? undefined
                  : (slotProps) => (
                    <DatePickerHeader
                      {...slotProps}
                      date={pickerValue ?? referenceDate ?? dayjs()}
                      setDate={setSelectedDate}
                    />
                  ),
              popper: StyledPopper,
              day: CustomStyledDay,
            }}
            slotProps={{
              actionBar: {
                actions: actionBarProps,
              },
              openPickerButton: {
                disableRipple: true,
              },
            }}
          />
        </LocalizationProvider>
        <StyledSupportingText
          error={error || errorReason === 'minTime' || errorReason === 'minDate' || errorReason === 'maxDate'}
        >
          {supportingText}
          {errorReason === 'minTime' && t('general.error.endAfterStart')}
          {errorReason === 'minDate' && t('general.error.endAfterStart')}
          {errorReason === 'maxDate' && t('general.error.timePeriodToSmall')}
        </StyledSupportingText>
      </StyledDatePickerContainer>
    );
  }
);

export default DatepickerTsoPq;
